import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Terminal, Sparkles, Send, Layers, Handshake, Zap, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import api from "@/lib/api";

const MARQUEE = ["AI GENERATION", "DRAG · EDIT · SHIP", "CLIENT HANDOFF", "NO CODE", "LIVE PREVIEW", "TEMPLATES", "AI CHAT EDITS"];

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans((r.data.plans || []).filter((p) => !p.hidden))).catch(() => {});
  }, []);

  const start = () => {
    if (prompt.trim()) localStorage.setItem("bf_seed_prompt", prompt.trim());
    nav(user ? "/dashboard" : "/register");
  };

  return (
    <div className="min-h-screen bg-[#f2f0eb] text-[#0a2540]">
      <Seo path="/" description="BuildForged is the AI website studio for agencies and freelancers. Generate complete, modern websites from a prompt, refine them in a live editor, add an AI salesperson that captures leads, and hand off to clients in minutes." />
      {/* header */}
      <header className="backdrop-blur-xl bg-[#f2f0eb]/80 border-b border-[#d3d3d3] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 font-heading font-black text-xl tracking-tight">
            <div className="w-7 h-7 bg-[#ff3b00] flex items-center justify-center text-white"><Layers size={16} /></div>
            BuildForged
          </div>
          <nav className="hidden md:flex items-center gap-8 label-tech">
            <a href="#how" className="hover:text-[#ff3b00]">How it works</a>
            <a href="#handoff" className="hover:text-[#ff3b00]">Handoff</a>
            <Link to="/pricing" className="hover:text-[#ff3b00]">Pricing</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login-link" className="label-tech hover:text-[#ff3b00]">Log in</Link>
            <button data-testid="nav-cta-btn" onClick={() => nav(user ? "/dashboard" : "/register")} className="bg-[#0a2540] text-white text-sm font-semibold px-5 py-2.5 hover:bg-[#ff3b00] transition-colors">
              {user ? "Dashboard" : "Get started"}
            </button>
          </div>
        </div>
      </header>

      {/* hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 blueprint-grid opacity-70" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-16 md:pt-28">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-4xl">
            <div className="label-tech mb-5 flex items-center gap-2"><Terminal size={13} /> AI WEBSITE STUDIO · BUILD → HANDOFF</div>
            <h1 className="font-heading font-black tracking-tighter leading-[0.95] text-5xl sm:text-6xl md:text-7xl">
              Design sites with AI. <span className="text-[#ff3b00]">Hand them to clients.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[#475569] max-w-2xl">
              Type an idea, watch a full website assemble itself, tweak it in the visual studio, then hand the keys to your customer so they maintain it themselves — no code, just chat.
            </p>
          </motion.div>

          {/* prompt box */}
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6 }} className="mt-10 max-w-2xl">
            <div className="bg-white border-2 border-[#0055ff] shadow-[0_0_25px_rgba(0,85,255,0.12)] p-2 flex items-center gap-2">
              <Sparkles size={18} className="ml-3 text-[#0055ff] shrink-0" />
              <input
                data-testid="hero-prompt-input"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && start()}
                placeholder="A modern bakery in Brooklyn with online ordering…"
                className="flex-1 bg-transparent font-tech text-sm py-3 outline-none placeholder:text-gray-400"
              />
              <button data-testid="hero-generate-btn" onClick={start} className="bg-[#ff3b00] text-white font-semibold px-5 py-3 flex items-center gap-2 hover:bg-[#e63500] transition-colors">
                Build <Send size={16} />
              </button>
            </div>
            <div className="label-tech mt-3 opacity-60">Powered by Claude Sonnet 4.6</div>
          </motion.div>
        </div>

        {/* marquee */}
        <div className="border-y border-[#d3d3d3] bg-[#f2f0eb] py-3 overflow-hidden">
          <div className="marquee-track">
            {[...MARQUEE, ...MARQUEE].map((m, i) => (
              <span key={i} className="label-tech mx-8 whitespace-nowrap">✦ {m}</span>
            ))}
          </div>
        </div>
      </section>

      {/* how it works */}
      <section id="how" className="max-w-7xl mx-auto px-6 py-24">
        <div className="label-tech mb-3">/ HOW IT WORKS</div>
        <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight max-w-2xl">Three moves from idea to a site your client owns.</h2>
        <div className="mt-12 grid md:grid-cols-3 gap-8">
          {[
            { n: "01", icon: Sparkles, t: "Generate", d: "Describe the business. Claude builds a full multi-section site with real copy, layout and colors." },
            { n: "02", icon: Zap, t: "Refine in studio", d: "Edit text, swap images and colors visually — or just tell the AI chat what to change." },
            { n: "03", icon: Handshake, t: "Hand off", d: "Assign the site to your customer. They log in and maintain it themselves via a simple dashboard." },
          ].map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-white border border-[#0a2540] shadow-hard p-8">
              <div className="flex items-center justify-between mb-6">
                <span className="font-tech text-sm text-[#ff3b00]">{s.n}</span>
                <s.icon size={22} className="text-[#0a2540]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading font-bold text-xl mb-2">{s.t}</h3>
              <p className="text-[#475569] text-sm leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* handoff band */}
      <section id="handoff" className="bg-[#0a2540] text-white">
        <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="label-tech text-white/50 mb-3">/ THE HANDOFF</div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight">You build it once. They run it forever.</h2>
            <p className="mt-5 text-white/70 leading-relaxed">Perfect for agencies and freelancers. Ship a polished site, then give the client a friendly dashboard where they can update copy, images and colors — or ask the built-in AI to "change my hero to blue" — without ever touching code or calling you.</p>
            <button onClick={() => nav("/register")} className="mt-8 bg-[#ff3b00] text-white font-semibold px-6 py-3.5 flex items-center gap-2 hover:bg-[#e63500] transition-colors">Start building free <ArrowRight size={18} /></button>
          </div>
          <div className="bg-white/5 border border-white/10 p-6 font-tech text-sm">
            <div className="text-white/40">customer@client.com ~ %</div>
            <div className="mt-3 text-[#0055ff]">$ ai: change the headline to "Now open weekends"</div>
            <div className="mt-2 text-white/80">✓ Updated hero heading.</div>
            <div className="mt-2 text-[#0055ff]">$ ai: make the buttons green</div>
            <div className="mt-2 text-white/80">✓ Primary color set to green.</div>
            <div className="mt-4 text-white/40 animate-pulse">▍</div>
          </div>
        </div>
      </section>

      {/* pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <div>
            <div className="label-tech mb-3">/ PRICING</div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight max-w-xl">Start free. Pay when you hand off to clients.</h2>
          </div>
          <Link to="/pricing" className="label-tech hover:text-[#ff3b00] flex items-center gap-1">See full pricing <ArrowRight size={13} /></Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 items-start">
          {plans.map((p) => {
            const featured = p.popular;
            return (
              <div key={p.id} className={`bg-white border p-8 flex flex-col h-full ${featured ? "border-[#ff3b00] shadow-hard-signal lg:-translate-y-2" : "border-[#0a2540] shadow-hard"}`}>
                {featured && <div className="label-tech text-[#ff3b00] mb-2">★ MOST POPULAR</div>}
                <div className="font-heading font-bold text-xl">{p.name}</div>
                <div className="text-sm text-[#475569] mt-1 min-h-[40px]">{p.tagline}</div>
                <div className="mt-5 mb-6">
                  {p.contact_sales
                    ? <span className="font-heading font-black text-3xl">Custom</span>
                    : <><span className="font-heading font-black text-4xl">${p.price}</span><span className="text-[#475569]">/mo</span></>}
                </div>
                <ul className="space-y-2.5 flex-1 mb-8">
                  {p.features.map((f, k) => (<li key={k} className="flex items-start gap-2 text-sm"><Check size={15} className="text-[#ff3b00] mt-0.5 shrink-0" /> {f}</li>))}
                </ul>
                <button onClick={() => nav("/pricing")} className={`w-full py-3 font-semibold transition-colors ${featured ? "bg-[#ff3b00] text-white hover:bg-[#e63500]" : "bg-[#0a2540] text-white hover:bg-[#ff3b00]"}`}>{p.contact_sales ? "Contact sales" : `Choose ${p.name}`}</button>
              </div>
            );
          })}
        </div>
      </section>

      <footer className="border-t border-[#d3d3d3]">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col md:flex-row justify-between gap-4 label-tech opacity-60">
          <span>© 2026 BUILDFORGED STUDIO</span>
          <span>AI WEBSITE BUILDER · MADE FOR AGENCIES</span>
        </div>
      </footer>
    </div>
  );
}
